import { assets } from "./assets";

const media = {
  organic: {
    hero: assets.qa.webshopPipelines,
    evidence: [
      assets.qa.webshopJob,
      {
        src: {
          de: assets.qa.webshopFlowDe,
          en: assets.qa.webshopFlowEn
        },
        mobileSrc: {
          de: assets.qa.webshopFlowMobileDe,
          en: assets.qa.webshopFlowMobileEn
        },
        kind: "diagram",
        width: 1400,
        height: 520,
        mobileWidth: 720,
        mobileHeight: 560
      }
    ]
  },
  monitoring: {
    hero: assets.qa.monitoringHealth,
    evidence: [
      assets.qa.monitoringWorkflow,
      assets.qa.monitoringDetail
    ]
  }
};

const sharedSectionOrder = [
  "overview",
  "architecture",
  "contribution",
  "coverage",
  "scenarios",
  "evidence",
  "implementedDecisions",
  "actions"
];

const projects = {
  organic: {
    slug: "organic-chemistry-webshop-automation",
    de: {
      title: "Webshop-Testautomatisierung",
      shortTitle: "Webshop-Testautomatisierung",
      category: "Java · Selenium · TestNG",
      seo: {
        title: "Webshop-Testautomatisierung mit Selenium und TestNG – Anton Rost",
        description: "Anonymisierte berufliche Fallstudie über datengetriebene Browser-Regression mit Java, Selenium, TestNG, GitLab CI und Allure."
      },
      imageAlt: "Nachgebildete GitLab-Pipeline für eine anonymisierte Selenium-Testautomatisierung",
      sectionOrder: sharedSectionOrder,
      overview: {
        title: "Worum es ging",
        lead: "Wichtige Shop-Abläufe liefen über mehrere Seiten, Kontozustände und abhängige Systeme. Einzelne UI-Checks reichten dafür nicht aus.",
        items: [
          ["Ausgangslage", "Suche, Produktdaten, Anfragen und Checkout mussten als zusammenhängende Abläufe geprüft werden."],
          ["Aufgabe", "Eine wiederholbare Regression aufbauen und fehlgeschlagene CI-Läufe verständlich machen."]
        ],
        disclosure: "Anonymisierte Rekonstruktion beruflicher Engineering-Arbeit. Namen, Daten, Kennzahlen, Ansichten und Systemdetails wurden für dieses Portfolio neu erstellt oder abstrahiert."
      },
      architecture: {
        title: "Von der Pipeline zur Evidenz",
        intro: "Der Test-Job startet die Suite. Maven, Surefire und TestNG steuern danach den Browserlauf und seine Ausgaben.",
        slides: [[0, 3], [2, 6], [5, 9]],
        stages: [
          {
            label: "Pipeline",
            nodes: [
              ["GitLab CI", "Startet Build, Test und anschließende Ausgaben in ihrer Reihenfolge."]
            ]
          },
          {
            label: "Build-Job",
            nodes: [
              ["Maven Compile", "Kompiliert das Java-Testprojekt im CI-Runner."]
            ]
          },
          {
            label: "Test-Job",
            nodes: [
              ["Lauf vorbereiten", "Wählt die Umgebung, startet die virtuelle Anzeige und legt Evidenzordner an."]
            ]
          },
          {
            label: "Eingaben",
            nodes: [
              ["Umgebung", "Übergibt die gewählte Testumgebung an den Lauf."],
              ["Tabellen-Fixtures", "Über Apache werden die passenden Datensätze als Excel bereitgestellt."]
            ]
          },
          {
            label: "Test-Runner",
            nodes: [
              ["Maven → Surefire → TestNG", "Die Maven-Testphase startet Surefire; Surefire liest die Suite und übergibt sie an TestNG."]
            ]
          },
          {
            label: "Browser-Setup",
            nodes: [
              ["ChromeDriver", "Das gemeinsame Setup öffnet die Chrome-Sitzung und richtet die Browser-Wartezeiten ein."]
            ]
          },
          {
            label: "Browser-Abläufe",
            nodes: [
              ["Zugang & Konto", "Selenium prüft Anmeldung und Kontozustände."],
              ["Produkt & Dokumente", "Selenium prüft Suche, Angaben, Sprache und Dokumentpfade."],
              ["Transaktion & Anfrage", "Selenium prüft Warenkorb, Checkout und Formulare."]
            ]
          },
          {
            label: "Evidenz",
            nodes: [
              ["Laufergebnisse", "Surefire, Allure, Screenshots und bereinigte Logs bleiben auch bei Fehlern erhalten."]
            ]
          },
          {
            label: "Ausgabe",
            nodes: [
              ["Allure-Bericht", "Ein Deploy-Job erzeugt und veröffentlicht die lesbare Ergebnisansicht."],
              ["Monitoring-Übergabe", "Ein zweiter Deploy-Job übergibt das Artefaktpaket an das QA-Reporting."]
            ]
          }
        ]
      },
      contribution: {
        title: "Mein Anteil",
        items: [
          ["Kritische Abläufe gemeinsam analysiert", "Login, Suche, Produktdaten, Anfragen, Warenkorb und Checkout als Prüfpunkte festgelegt."],
          ["Testdaten je Umgebung gepflegt", "Passende Tabellenwerte pro Umgebung, ohne die Tests umzuschreiben."],
          ["Headless Chrome im GitLab Runner", "TestNG, Allure, Screenshots und Logs im Serverlauf zusammengeführt."],
          ["Robuste Selektoren gepflegt", "Stabile IDs bevorzugt, CSS gezielt genutzt und XPath nur eingesetzt, wenn nötig."]
        ]
      },
      coverage: {
        title: "Was die Suite abdeckt",
        caption: "Anonymisierte Übersicht der abgedeckten Geschäftsbereiche",
        headings: ["Bereich", "Warum wichtig", "Was geprüft wird", "Evidenz"],
        rows: [
          ["Zugang & Konto", "Der richtige Kontozustand muss erreichbar bleiben.", "Login, Logout, Wiederherstellung und geschützte Zustände", "Status und Screenshot"],
          ["Suche & Produktdaten", "Falsche Treffer oder Angaben führen zur falschen Auswahl.", "Suche, Vorschläge, Filter und Produktmerkmale", "Allure und Screenshot"],
          ["Dokumente & Sprache", "Produktangaben, Verknüpfungen und Sprache müssen zusammenpassen.", "Verfügbarkeit, Zuordnung und englische Darstellung", "Browserstatus und Kontext"],
          ["Warenkorb & Checkout", "Menge, Preis und Abschluss dürfen nicht auseinanderlaufen.", "Mengenregeln, Preiszustand, Warenkorb und Checkout", "Assertions und Ergebnisdatei"],
          ["Kontakt & Anfrage", "Formular- oder Uploaddaten dürfen nicht verloren gehen.", "Pflichtfelder, Zustimmung, Upload und Bestätigung", "Ablauf und Screenshot"],
          ["Abhängige Zustände", "Änderungen aus anderen Systemen müssen im Shop ankommen.", "Sichtbares Verhalten nach kontrolliertem Zustandswechsel", "Browserstatus und Diagnose"]
        ]
      },
      scenarios: {
        title: "Drei typische Prüfpfade",
        intro: "Die Beispiele nutzen ausschließlich erfundene Daten.",
        items: [
          {
            tab: "Produkt & Dokument",
            title: "Produktdaten abgleichen",
            startingPoint: "Ein fiktives technisches Produkt ist für die Testumgebung vorbereitet.",
            steps: ["Produkt suchen", "Ergebnis filtern", "Detailseite öffnen", "Angaben und Dokumentbezug vergleichen"],
            outcomes: ["Treffer und Filter passen zusammen", "Die sichtbaren Kerndaten stimmen", "Die erwartete Dokumentaktion ist verfügbar"],
            evidence: "TestNG-Status, Allure-Schritte und Screenshot"
          },
          {
            tab: "Konto & Warenkorb",
            title: "Preis und Warenkorb prüfen",
            startingPoint: "Ein fiktives Geschäftskonto und ein leerer Warenkorb stehen bereit.",
            steps: ["Anmelden", "Produkt und Menge wählen", "Preis und Warenkorb prüfen", "Den kontrollierten Checkout durchlaufen"],
            outcomes: ["Geschützte Informationen erscheinen erst nach Login", "Menge und sichtbarer Preis bleiben konsistent", "Der vorgesehene Abschlusszustand wird erreicht"],
            evidence: "TestNG, Diagnosekontext und Bestätigungsansicht"
          },
          {
            tab: "Individuelle Anfrage",
            title: "Anfrage mit Testdatei senden",
            startingPoint: "Fiktive Formulardaten und eine harmlose PDF-Fixture liegen vor.",
            steps: ["Anfrage öffnen", "Testdaten eintragen", "PDF auswählen", "Formular absenden"],
            outcomes: ["Pflichtfelder reagieren verständlich", "Die gewählte Datei bleibt sichtbar", "Ein Bestätigungszustand wird erreicht"],
            evidence: "Allure-Schritte, Uploadstatus und Screenshot"
          }
        ]
      },
      evidence: {
        title: "Was ein Lauf hinterlässt",
        items: [
          ["Job und Testergebnis", "Status, Testausgabe und Artefakte bleiben im CI-Job greifbar.", "Nachgebildete GitLab-Jobansicht mit abstrahierter Testausgabe, Status und Artefakten"],
          ["CI-Ablauf", "Vom Testlauf bis zu den Ergebnissen bleibt der Weg nachvollziehbar.", "Vereinfachter CI-Ablauf von den Testdaten bis zu Bericht und Monitoring"]
        ]
      },
      implementedDecisions: {
        title: "Entscheidungen, die mir wichtig waren",
        items: [
          ["Geschäftsablauf vor Einzelprüfung", "Die Tests folgen dem Ziel des Nutzers, nicht einzelnen Seitenelementen."],
          ["Gleicher Flow, andere Daten", "Umgebungswerte ändern sich, die Browserlogik bleibt wiederverwendbar."],
          ["Fehler müssen erklärbar sein", "Berichte, Screenshots und Logs bleiben nach einem fehlgeschlagenen Lauf greifbar."]
        ]
      },
      actions: {
        title: "Lebenslauf und Kontakt",
        intro: "Im Lebenslauf steht mehr zu meiner Erfahrung. Für Rückfragen bin ich direkt erreichbar.",
        primary: "Lebenslauf ansehen",
        secondary: "Kontakt aufnehmen"
      }
    },
    en: {
      title: "Webshop Test Automation",
      shortTitle: "Webshop Test Automation",
      category: "Java · Selenium · TestNG",
      seo: {
        title: "Webshop test automation with Selenium and TestNG – Anton Rost",
        description: "An anonymized professional case study about data-driven browser regression with Java, Selenium, TestNG, GitLab CI, and Allure."
      },
      imageAlt: "Recreated GitLab pipeline for anonymized Selenium test automation",
      sectionOrder: sharedSectionOrder,
      overview: {
        title: "What the work was about",
        lead: "Important webshop journeys crossed several pages, account states, and dependent systems. Isolated UI checks were not enough.",
        items: [
          ["Situation", "Search, product data, enquiries, and checkout needed to be tested as connected journeys."],
          ["Objective", "Build repeatable regression and make failed CI runs easier to understand."]
        ],
        disclosure: "An anonymized reconstruction of professional engineering work. Names, data, metrics, interfaces, and system details were recreated or abstracted for this portfolio."
      },
      architecture: {
        title: "From pipeline to evidence",
        intro: "The test job starts the suite. Maven, Surefire, and TestNG then control the browser run and its outputs.",
        slides: [[0, 3], [2, 6], [5, 9]],
        stages: [
          {
            label: "Pipeline",
            nodes: [
              ["GitLab CI", "Starts build, test, and the subsequent outputs in sequence."]
            ]
          },
          {
            label: "Build job",
            nodes: [
              ["Maven compile", "Compiles the Java test project in the CI runner."]
            ]
          },
          {
            label: "Test job",
            nodes: [
              ["Prepare the run", "Selects the environment, starts the virtual display, and creates evidence directories."]
            ]
          },
          {
            label: "Inputs",
            nodes: [
              ["Environment", "Passes the selected test environment into the run."],
              ["Spreadsheet fixtures", "Apache supplies the matching data sets via Excel to the test methods."]
            ]
          },
          {
            label: "Test runner",
            nodes: [
              ["Maven → Surefire → TestNG", "The Maven test phase starts Surefire; Surefire reads the suite and hands it to TestNG."]
            ]
          },
          {
            label: "Browser setup",
            nodes: [
              ["ChromeDriver", "Shared setup opens the Chrome session and configures the browser waits."]
            ]
          },
          {
            label: "Browser journeys",
            nodes: [
              ["Access & account", "Selenium checks sign-in and account states."],
              ["Product & documents", "Selenium checks search, details, language, and document paths."],
              ["Transaction & enquiry", "Selenium checks basket, checkout, and forms."]
            ]
          },
          {
            label: "Evidence",
            nodes: [
              ["Run results", "Surefire, Allure, screenshots, and cleaned logs remain available after failures."]
            ]
          },
          {
            label: "Output",
            nodes: [
              ["Allure report", "One deploy job builds and publishes the readable result view."],
              ["Monitoring handoff", "A second deploy job passes the artifact bundle to QA reporting."]
            ]
          }
        ]
      },
      contribution: {
        title: "My contribution",
        items: [
          ["Analysed critical journeys together", "Defined sign-in, search, product data, enquiries, basket, and checkout as checks."],
          ["Maintained test data per environment", "Spreadsheet values vary by environment while the browser journeys stay unchanged."],
          ["Ran headless Chrome in GitLab", "Brought TestNG, Allure, screenshots, and logs together in the runner."],
          ["Maintained robust selectors", "Preferred stable IDs, used CSS deliberately, and reserved XPath for gaps."]
        ]
      },
      coverage: {
        title: "What the suite covers",
        caption: "An anonymized overview of the covered business areas",
        headings: ["Area", "Why it matters", "What is checked", "Evidence"],
        rows: [
          ["Access & account", "The correct account state must remain reachable.", "Sign-in, sign-out, recovery, and protected states", "Status and screenshot"],
          ["Search & product data", "Incorrect results or details can lead to the wrong selection.", "Search, suggestions, filters, and product attributes", "Allure and screenshot"],
          ["Documents & language", "Product details, links, and language must agree.", "Availability, relationship, and English presentation", "Browser state and context"],
          ["Basket & checkout", "Quantity, price, and completion must stay consistent.", "Quantity rules, visible price, basket, and checkout", "Assertions and result file"],
          ["Contact & enquiry", "Form or upload information must not be lost.", "Required fields, consent, upload, and confirmation", "Journey and screenshot"],
          ["Dependent states", "Changes from another system must reach the webshop.", "Visible behavior after a controlled state change", "Browser state and diagnostics"]
        ]
      },
      scenarios: {
        title: "Three representative checks",
        intro: "Every example uses invented data.",
        items: [
          {
            tab: "Product & document",
            title: "Compare product data",
            startingPoint: "A fictional technical product is prepared in the test environment.",
            steps: ["Search for the product", "Filter the result", "Open the detail page", "Compare details and document relationship"],
            outcomes: ["Result and filter agree", "The visible core data matches", "The expected document action is available"],
            evidence: "TestNG status, Allure steps, and screenshot"
          },
          {
            tab: "Account & basket",
            title: "Check price and basket",
            startingPoint: "A fictional business account and an empty basket are ready.",
            steps: ["Sign in", "Choose product and quantity", "Check price and basket", "Follow the controlled checkout path"],
            outcomes: ["Protected information appears after sign-in", "Quantity and visible price stay consistent", "The intended completion state is reached"],
            evidence: "TestNG, diagnostic context, and confirmation view"
          },
          {
            tab: "Custom enquiry",
            title: "Submit an enquiry with a test file",
            startingPoint: "Fictional form data and a harmless PDF fixture are ready.",
            steps: ["Open the enquiry", "Enter test data", "Select the PDF", "Submit the form"],
            outcomes: ["Required fields respond clearly", "The selected file remains visible", "A confirmation state is reached"],
            evidence: "Allure steps, upload state, and screenshot"
          }
        ]
      },
      evidence: {
        title: "What a run leaves behind",
        items: [
          ["Job and test result", "Status, test output, and artifacts remain available in the CI job.", "Recreated GitLab job view with abstracted test output, state, and artifacts"],
          ["CI flow", "The path from test run to results stays easy to follow.", "Simplified CI flow from test data to reporting and monitoring"]
        ]
      },
      implementedDecisions: {
        title: "Decisions that mattered",
        items: [
          ["Business journey before isolated check", "The tests follow the user's goal rather than individual page elements."],
          ["Same flow, different data", "Environment values change while the browser logic remains reusable."],
          ["Failures must stay explainable", "Reports, screenshots, and logs remain available after a failed run."]
        ]
      },
      actions: {
        title: "Résumé and contact",
        intro: "My résumé covers more of my experience. I am also easy to reach with questions.",
        primary: "View résumé",
        secondary: "Get in touch"
      }
    }
  },
  monitoring: {
    slug: "quality-operations-monitoring-dashboard",
    de: {
      title: "Quality Operations Monitoring",
      shortTitle: "Quality Operations Monitoring",
      category: "n8n · Grafana · QA Reporting",
      seo: {
        title: "Quality Operations Monitoring mit n8n und Grafana – Anton Rost",
        description: "Anonymisierte berufliche Fallstudie über n8n-Workflows und Grafana-Ansichten für funktionale Tests, visuelle Reviews, Synchronisation und Übertragung."
      },
      imageAlt: "Nachgebildete Grafana-Übersicht mit vier anonymisierten QA- und Betriebssignalen",
      sectionOrder: sharedSectionOrder,
      overview: {
        title: "Worum es ging",
        lead: "Testergebnisse, visuelle Reviews, Synchronisation und Übertragungsstatus lagen in verschiedenen Systemen. Für die tägliche QA-Arbeit fehlte eine gemeinsame Sicht.",
        items: [
          ["Ausgangslage", "Vier Quellen hatten eigene Formate, Zustände und Wege zur Fehlersuche."],
          ["Aufgabe", "Signale zusammenführen, ihre Bedeutung erhalten und den Weg ins Detail verkürzen."]
        ],
        disclosure: "Anonymisierte Rekonstruktion beruflicher Engineering-Arbeit. Namen, Daten, Kennzahlen, Ansichten und Systemdetails wurden für dieses Portfolio neu erstellt oder abstrahiert."
      },
      architecture: {
        title: "Wie die vier Signale Grafana erreichen",
        intro: "Der funktionale Lauf wird zuerst importiert. Die Panels lesen ihn und drei weitere Quellen anschließend über getrennte n8n-Routen.",
        slides: [
          { range: [0, 4], incoming: false, outgoing: true },
          { range: [4, 7], incoming: true, outgoing: true },
          { range: [7, 9], incoming: true, outgoing: true },
          { range: [9, 11], incoming: true, outgoing: false }
        ],
        stages: [
          {
            label: "CI-Artefakt",
            nodes: [
              ["Testlauf", "Der Pipeline-Job hinterlässt das gebündelte funktionale Testergebnis."]
            ]
          },
          {
            label: "Funktionaler Import",
            nodes: [
              ["n8n-Import", "Ein zeitgesteuerter Lauf oder ein Webshop-Deployment liest das Artefakt ein."]
            ]
          },
          {
            label: "Aufbereitung",
            nodes: [
              ["Entpacken & Parsen", "Der Workflow entpackt die Übergabe und liest die benötigten XML-Ergebnisse."]
            ]
          },
          {
            label: "Gespeichertes Ergebnis",
            nodes: [
              ["Aktueller Testlauf", "Derzeit hält n8n das aufbereitete Ergebnis in SQLite bereit. PostgreSQL wäre der nächste Schritt für eine dauerhafte Historie."]
            ]
          },
          {
            label: "Grafana-Aufruf",
            nodes: [
              ["Panels laden", "Die benutzerdefinierten Ansichten fordern ihre vier Signale an."]
            ]
          },
          {
            label: "Geschützte n8n-Routen",
            nodes: [
              ["Funktion", "Liest den zuletzt importierten Testlauf."],
              ["Visual", "Ruft den aktuellen Reviewzustand ab."],
              ["Sync", "Liest die vorgesehenen Daten über einen geschützten VPN-Tunnel mit Schlüssel-Authentifizierung."],
              ["Queue", "Liest den Übertragungsstatus über denselben geschützten VPN-Zugang."]
            ]
          },
          {
            label: "Quellzugriff",
            nodes: [
              ["Gespeicherter Testlauf", "Liefert das zuvor aufbereitete funktionale Ergebnis."],
              ["Visual-Service", "Liefert den neuesten Build- und Reviewzustand."],
              ["Sync-Datenbank", "Liefert Übersicht und ausgewählte Detailgruppen."],
              ["Queue-Service", "Liefert nach Authentifizierung Zähler und Fehlerkontext."]
            ]
          },
          {
            label: "Zentrale Einordnung in n8n",
            nodes: [
              ["Statusmodell", "Zentral in n8n: Gemeinsam entwickelte Regeln und Schwellen ergeben je Quelle Healthy, Warning oder Critical für Grafana."]
            ]
          },
          {
            label: "Getrennte Antworten",
            nodes: [
              ["Funktion", "Gibt Teststatus und Evidenzhinweis zurück."],
              ["Visual", "Gibt Build- und Reviewzustand zurück."],
              ["Sync", "Gibt den zusammengefassten Verarbeitungszustand zurück."],
              ["Queue", "Gibt Übertragungszustand und Diagnosehinweis zurück."]
            ]
          },
          {
            label: "Grafana-Panels",
            nodes: [
              ["Funktion", "Zeigt Browser-Teststatus und Weg zur Evidenz."],
              ["Visual", "Zeigt den visuellen Review getrennt davon."],
              ["Sync", "Zeigt den Stand der lokalen Verarbeitung."],
              ["Queue", "Zeigt den Stand der nachgelagerten Übertragung."]
            ]
          },
          {
            label: "Nutzung",
            nodes: [
              ["Übersicht & Details", "Ordnet die vier Quellen nebeneinander ein, ohne ihre Zustände zu vermischen."]
            ]
          }
        ]
      },
      contribution: {
        title: "Mein Anteil",
        items: [
          ["Quellen angebunden", "Testartefakte, Percy, Datenbankabfragen und Queue-API."],
          ["n8n-Workflows gebaut", "Antworten aufbereitet und mit Projektmanagement, QA und Geschäftsführung entwickelte Statusregeln zentral gepflegt."],
          ["Grafana-Ansichten entwickelt", "Übersicht und Fehlersuche mit eigenem HTML und JavaScript."],
          ["Dienste bereitgestellt", "n8n und Grafana containerisiert, hinter Reverse Proxy und geschütztem VPN-Zugang."]
        ]
      },
      coverage: {
        title: "Was jede Quelle beiträgt",
        caption: "Anonymisierte Übersicht der vier Signalquellen",
        headings: ["Signal", "Frage", "Was sichtbar wird", "Was getrennt bleibt"],
        rows: [
          ["Funktionale Tests", "Funktionieren die wichtigen Browser-Abläufe?", "Laufstatus, betroffener Bereich und Evidenzhinweis", "Ein Funktionsfehler ist nicht automatisch eine visuelle Änderung."],
          ["Visuelle Reviews", "Muss eine Oberflächenänderung geprüft werden?", "Reviewzustand und Referenz zum Build", "Eine unveränderte Ansicht beweist keine korrekte Funktion."],
          ["Produktsynchronisation", "Wurde die lokale Verarbeitung abgeschlossen?", "Zusammengefasster Zustand der letzten Verarbeitung", "Lokaler Erfolg beweist noch keine Übertragung."],
          ["Übertragungsqueue", "Erreichen Änderungen das nachgelagerte System?", "Queue- und Fehlerzustand für die weitere Prüfung", "Die Queue überschreibt nicht den Sync-Status."]
        ]
      },
      scenarios: {
        title: "Drei typische Situationen",
        intro: "Alle Zustände und Zeitangaben sind erfunden.",
        items: [
          {
            tab: "Funktion & Visual",
            title: "Funktion rot, Review unverändert",
            startingPoint: "Ein Browser-Test schlägt fehl, während Percy keine passende Änderung zeigt.",
            steps: ["Beobachtungskontext vergleichen", "Funktionale Evidenz öffnen", "Visuellen Review prüfen", "Bei der Verhaltenslogik weiterarbeiten"],
            outcomes: ["Der Funktionsstatus bleibt kritisch", "Der visuelle Status bleibt eigenständig", "Kein Signal überschreibt das andere"],
            evidence: "Quellkarten und bereinigte Referenz"
          },
          {
            tab: "Sync & Queue",
            title: "Sync erfolgreich, Queue verzögert",
            startingPoint: "Die lokale Verarbeitung ist abgeschlossen, die Übertragung bleibt jedoch zurück.",
            steps: ["Beide Quellzustände vergleichen", "Lokale Verarbeitung bestätigen", "Übertragungsqueue öffnen", "Transportpfad als nächsten Prüfpunkt festhalten"],
            outcomes: ["Sync und Übertragung bleiben getrennt", "Der Zusammenhang ist sichtbar", "Der nächste Schritt folgt der betroffenen Quelle"],
            evidence: "Statusverlauf und Detailhinweis"
          },
          {
            tab: "Review-Kontext",
            title: "Technischer Status in Bearbeitung",
            startingPoint: "Ein Quellstatus wird untersucht oder befindet sich in einem geplanten Wartungsfenster.",
            steps: ["Technischen Zustand prüfen", "Arbeitskontext ergänzen", "Quellstatus sichtbar lassen", "Kontext nach Abschluss entfernen"],
            outcomes: ["Der Arbeitskontext ersetzt keine Evidenz", "Bearbeitung bleibt sichtbar", "Der technische Status bleibt nachvollziehbar"],
            evidence: "Statuskarte und Bearbeitungshinweis"
          }
        ]
      },
      evidence: {
        title: "Wie die Ansicht bei der Einordnung hilft",
        items: [
          ["Workflow und Einordnung", "n8n liest Quellen ein, bereitet Antworten auf und ordnet den Status zentral ein.", "Nachgebildeter n8n-Workflow mit abstrahierten Knoten für Quellen, Aufbereitung und Statusklassifikation"],
          ["Vom Status ins Detail", "Die Detailansicht hält Signal, Kontext und nächsten Prüfschritt zusammen.", "Nachgebildete Grafana-Detailansicht mit abstrahierten Statuswerten und Diagnosebereichen"]
        ]
      },
      implementedDecisions: {
        title: "Entscheidungen, die mir wichtig waren",
        items: [
          ["Quellen getrennt lassen", "Funktionsfehler, visuelle Reviews und Queue-Probleme sind verschiedene Tatsachen."],
          ["Übersicht und Detail verbinden", "Der erste Blick bleibt kompakt; der Kontext liegt eine Ebene tiefer."],
          ["Statusmodell zentral halten", "Die Einordnung passiert in n8n, nicht fest in Grafana. Regeln und Schwellen bleiben pro Workflow an einer Stelle pflegbar."]
        ]
      },
      actions: {
        title: "Lebenslauf und Kontakt",
        intro: "Im Lebenslauf steht mehr zu meiner Erfahrung. Für Rückfragen bin ich direkt erreichbar.",
        primary: "Lebenslauf ansehen",
        secondary: "Kontakt aufnehmen"
      }
    },
    en: {
      title: "Quality Operations Monitoring",
      shortTitle: "Quality Operations Monitoring",
      category: "n8n · Grafana · QA Reporting",
      seo: {
        title: "Quality Operations Monitoring with n8n and Grafana – Anton Rost",
        description: "An anonymized professional case study about n8n workflows and Grafana views for functional tests, visual reviews, synchronization, and transmission."
      },
      imageAlt: "Recreated Grafana overview with four anonymized QA and operational signals",
      sectionOrder: sharedSectionOrder,
      overview: {
        title: "What the work was about",
        lead: "Functional results, visual reviews, synchronization, and transmission state lived in different systems. Daily QA work needed a shared view.",
        items: [
          ["Situation", "Four sources had their own formats, states, and paths into investigation."],
          ["Objective", "Bring the signals together, preserve their meaning, and shorten the path into detail."]
        ],
        disclosure: "An anonymized reconstruction of professional engineering work. Names, data, metrics, interfaces, and system details were recreated or abstracted for this portfolio."
      },
      architecture: {
        title: "How the four signals reach Grafana",
        intro: "The functional run is imported first. The panels then read it and three other sources through separate n8n routes.",
        slides: [
          { range: [0, 4], incoming: false, outgoing: true },
          { range: [4, 7], incoming: true, outgoing: true },
          { range: [7, 9], incoming: true, outgoing: true },
          { range: [9, 11], incoming: true, outgoing: false }
        ],
        stages: [
          {
            label: "CI artifact",
            nodes: [
              ["Test run", "The pipeline job leaves the bundled functional result behind."]
            ]
          },
          {
            label: "Functional intake",
            nodes: [
              ["n8n import", "A scheduled run or a webshop deployment reads the artifact."]
            ]
          },
          {
            label: "Preparation",
            nodes: [
              ["Unpack & parse", "The workflow unpacks the handoff and reads the required XML results."]
            ]
          },
          {
            label: "Stored result",
            nodes: [
              ["Current test run", "n8n currently keeps the prepared result in SQLite. PostgreSQL would be the next step for durable history."]
            ]
          },
          {
            label: "Grafana request",
            nodes: [
              ["Panels load", "The custom views request their four signals."]
            ]
          },
          {
            label: "Protected n8n routes",
            nodes: [
              ["Functional", "Reads the latest imported test run."],
              ["Visual", "Fetches the current review state."],
              ["Sync", "Reads the intended data through a protected VPN tunnel with key-based authentication."],
              ["Queue", "Reads downstream transmission state through the same protected VPN access."]
            ]
          },
          {
            label: "Source access",
            nodes: [
              ["Stored test run", "Returns the previously prepared functional result."],
              ["Visual service", "Returns the latest build and review state."],
              ["Sync database", "Returns overview and selected detail groups."],
              ["Queue service", "Returns counts and error context after authentication."]
            ]
          },
          {
            label: "Central n8n classification",
            nodes: [
              ["Status model", "Central in n8n: jointly developed rules and thresholds assign each source Healthy, Warning, or Critical for Grafana."]
            ]
          },
          {
            label: "Separate responses",
            nodes: [
              ["Functional", "Returns test state and an evidence hint."],
              ["Visual", "Returns build and review state."],
              ["Sync", "Returns the summarized processing state."],
              ["Queue", "Returns transmission state and a diagnostic hint."]
            ]
          },
          {
            label: "Grafana panels",
            nodes: [
              ["Functional", "Shows browser-test state and the path to evidence."],
              ["Visual", "Shows visual review independently."],
              ["Sync", "Shows local processing state."],
              ["Queue", "Shows downstream transmission state."]
            ]
          },
          {
            label: "Use",
            nodes: [
              ["Overview & details", "Presents the four sources together without merging their states."]
            ]
          }
        ]
      },
      contribution: {
        title: "My contribution",
        items: [
          ["Connected sources", "Test artifacts, Percy, database queries, and queue API."],
          ["Built n8n workflows", "Prepared responses and maintained status rules developed with project management, QA, and leadership in one place."],
          ["Developed Grafana views", "Overview and investigation with custom HTML and JavaScript."],
          ["Deployed supporting services", "Containerized n8n and Grafana behind a reverse proxy and protected VPN access."]
        ]
      },
      coverage: {
        title: "What each source contributes",
        caption: "An anonymized overview of the four signal sources",
        headings: ["Signal", "Question", "What becomes visible", "What stays separate"],
        rows: [
          ["Functional tests", "Do the important browser journeys work?", "Run state, affected area, and evidence hint", "A functional failure is not automatically a visual change."],
          ["Visual reviews", "Does a UI change need review?", "Review state and build reference", "An unchanged view does not prove correct behavior."],
          ["Product synchronization", "Did local processing complete?", "Summarized state of the latest processing", "Local success does not prove downstream transmission."],
          ["Transmission queue", "Are changes reaching the downstream system?", "Queue and error state for further investigation", "Queue state does not overwrite synchronization state."]
        ]
      },
      scenarios: {
        title: "Three representative situations",
        intro: "Every state and timestamp is invented.",
        items: [
          {
            tab: "Function & visual",
            title: "Function fails, review unchanged",
            startingPoint: "A browser check fails while Percy shows no corresponding change.",
            steps: ["Compare observation context", "Open the functional evidence", "Check the visual review", "Continue with behavior rather than layout"],
            outcomes: ["Functional state remains critical", "Visual state remains independent", "Neither signal overwrites the other"],
            evidence: "Source cards and cleaned reference"
          },
          {
            tab: "Sync & queue",
            title: "Sync succeeds, queue is delayed",
            startingPoint: "Local processing completes while downstream transmission falls behind.",
            steps: ["Compare both source states", "Confirm local processing", "Open the transmission queue", "Record transport as the next investigation point"],
            outcomes: ["Synchronization and transmission remain separate", "Their relationship is visible", "The next step follows the affected source"],
            evidence: "State history and detail hint"
          },
          {
            tab: "Review context",
            title: "Technical state under review",
            startingPoint: "A source state is being investigated or sits inside planned maintenance.",
            steps: ["Check the technical state", "Add work context", "Keep source evidence visible", "Remove context when work is complete"],
            outcomes: ["Work context does not replace evidence", "Ongoing work remains visible", "Technical state stays traceable"],
            evidence: "State card and work note"
          }
        ]
      },
      evidence: {
        title: "How the view supports investigation",
        items: [
          ["Workflow and classification", "n8n reads the sources, prepares responses, and classifies state centrally.", "Recreated n8n workflow with abstracted nodes for sources, preparation, and state classification"],
          ["From state to detail", "The detail view keeps signal, context, and the next check together.", "Recreated Grafana detail view with abstracted status values and diagnostic areas"]
        ]
      },
      implementedDecisions: {
        title: "Decisions that mattered",
        items: [
          ["Keep sources separate", "Functional failures, visual reviews, and queue problems are different facts."],
          ["Connect overview and detail", "The first view stays compact; context sits one level deeper."],
          ["Keep the status model central", "Classification lives in n8n, not hard-coded in Grafana. Rules and thresholds stay editable per workflow."]
        ]
      },
      actions: {
        title: "Résumé and contact",
        intro: "My résumé covers more of my experience. I am also easy to reach with questions.",
        primary: "View résumé",
        secondary: "Get in touch"
      }
    }
  }
};

export const projectKeys = ["organic", "monitoring"];

export function getQaProject(locale, key) {
  const project = projects[key];
  return { ...(project[locale] || project.de), key, slug: project.slug, media: media[key] };
}
